import { isHash } from 'viem'

import { toCursorPage } from '@/helpers/cursorPage'
import { orpc } from '@/lib/orpc/client'
import { errorCodeOf } from '@/lib/orpc/errors'

import type { MarketTransactionsInput, MarketTransactionsOutput } from '../transactions.contract'

/** The procedure's input without the cursor; `marketId` is the page's route param. */
export type MarketTransactionsFilters = Omit<MarketTransactionsInput, 'cursor' | 'marketId'> & {
  marketId: string
}

/**
 * Viewer mode of the market activity table:
 * - `all`    → global market activity feed
 * - `viewer` → scoped to a wallet ("Your transactions")
 *
 * Appended to the procedure key so the disconnected viewer query
 * (`userAddress` normalized to `undefined`) never hydrates from the global
 * feed's cache entry, which shares the same `userAddress: undefined` filters.
 */
export type MarketTransactionsViewerScope = 'all' | 'viewer'

/** A market the bridge does not know has no transactions: the table shows its empty state. */
export const EMPTY_MARKET_TRANSACTIONS: MarketTransactionsOutput = {
  items: [],
  partial: false,
  nextCursor: null
}

// Unset filters are dropped so the server's first-page key and the hook's default key hash alike.
const definedFilters = (filters: MarketTransactionsFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined))

export const marketTransactionsTableQueryKey = (
  filters: MarketTransactionsFilters,
  viewerScope: MarketTransactionsViewerScope
) =>
  [
    ...orpc.market.transactions.key({ type: 'infinite' }),
    definedFilters(filters),
    viewerScope
  ] as const

export async function fetchMarketTransactionsPage(
  filters: MarketTransactionsFilters,
  cursor: string | undefined
) {
  const { marketId } = filters
  if (!isHash(marketId)) return toCursorPage(EMPTY_MARKET_TRANSACTIONS)
  try {
    return toCursorPage(await orpc.market.transactions.call({ ...filters, marketId, cursor }))
  } catch (error) {
    if (errorCodeOf(error) === 'NOT_FOUND') return toCursorPage(EMPTY_MARKET_TRANSACTIONS)
    throw error
  }
}
